import React, { useRef, useState } from 'react';
import { ContactRequestContext, RequestTypes } from 'types/ContactRequests';
import { ProductType } from 'types/Products';
import { EventSearchDocument } from 'types/Search';
import piwik from 'piwik/piwik';
import { Box, Button, Container, IconButton, Typography } from '@mui/material';
import RequestDocumentDialog from 'components/common/Requests/RequestDocumentDialog';
import RequestContactDialog from 'components/common/Requests/RequestContactDialog';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { COMPARE_BULK_LIMIT } from '../useSelection';
import SearchCompareModal from '../SearchCompareModal/SearchCompareModal';
import ToolTipContent from 'components/common/ToolTipContent';
import { tss } from 'tss-react/mui';
import { IS_UXBEAUTY } from 'components/constants/Constants';
import { useIfVerifiedUser } from 'hooks/useUserState_utils';
import { useTranslation } from 'react-i18next';
import { getProductTypeSingulars } from 'api/types/ReferenceData';
import { useComponentSizeFixed } from 'hooks/useComponentSizeFixed';
import RequestQuoteOrSampleDialog from 'components/common/Requests/RequestQuoteOrSampleDialog';
import { isProductOutOfStock } from 'components/pages/products/helpers/formAttributes';
import { UXB_EVENT_KEY } from 'supplier-portals/uxbeauty/constants';
import { SegmentTag } from 'types/global';
import { useSaveClickEvent } from 'components/pages/projects/helpers/ProductActions/ProductActionModals';
import { LogEventType } from 'types/LogEvents';
import { AddToCartDialog } from 'components/common/Requests/AddToCartDialog';
import ShoppingCartOutlined from '@mui/icons-material/ShoppingCartOutlined';

interface CompareRequestBulkRequestBarProps {
  compareModeActive: boolean;
  compareItemList: EventSearchDocument[];
  onClearAllClick: () => void;
  onRemoveItemClick: (itemId: string) => void;
  logCompareClick: () => void;
  searchRequest?: any;
  target: ProductType;
  searchContext?: boolean;
  event?: boolean;
  endCompareRequestMode: () => void;
  type: 'compare' | 'request' | undefined;
  segment: SegmentTag;
  setCompareBarHeight: Function;
  eventKey?: string;
}
export const CompareRequestBulkRequestBar: React.FC<
  CompareRequestBulkRequestBarProps
> = props => {
  const context = IS_UXBEAUTY
    ? {
        type: ContactRequestContext.SUPPLIER_PORTAL,
        portalKey: UXB_EVENT_KEY
      }
    : props.searchRequest?.eventKey
    ? {
        type: ContactRequestContext.BRAND_PORTAL,
        portalKey: props.searchRequest.eventKey
      }
    : {
        type: ContactRequestContext.COVALO
      };

  type RequestModalType =
    | 'sample'
    | 'quote'
    | 'contact'
    | 'requestDocument'
    | 'addToCart';

  const [requestModals, setRequestModals] = useState<
    RequestModalType | undefined
  >(undefined);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const saveClickEvent = useSaveClickEvent();
  const handleRequestModal = (
    type: RequestModalType,
    eventLog: LogEventType | null
  ) => {
    setRequestModals(type);
    eventLog &&
      saveClickEvent(
        { products: props.compareItemList, searchRequest: props.searchRequest },
        eventLog,
        props.segment,
        props.eventKey
      );
  };

  const onCompareClickHandler = () => {
    piwik.push(['trackEvent', 'Compare', 'open-compare-view']);
    setShowCompareModal(true);
    props.logCompareClick();
  };

  const onCompareModalHide = () => {
    setShowCompareModal(false);
  };
  const { classes, cx } = useStyles();
  const verified = useIfVerifiedUser();
  const { t } = useTranslation();

  const outOfStockProducts = props.compareItemList
    .filter(item =>
      isProductOutOfStock(
        item?.event?.product_private_attributes?.data?.inStock?.[0]
      )
    )
    .map(el => el.name);
  const isButtonDisabled = (requestType: RequestTypes) => {
    if (!props.compareItemList.length || !verified) return true;
    if (props.type === 'compare') return false;
    if (requestType !== RequestTypes.SAMPLE) return false;
    if (!IS_UXBEAUTY) return false;
    return outOfStockProducts.length > 0;
  };

  const requestRef = useRef<HTMLDivElement | null>(null);
  const { height: requestSectionHeight } = useComponentSizeFixed(requestRef);

  if (requestSectionHeight) {
    props.setCompareBarHeight(requestSectionHeight);
  }

  return (
    <>
      <Box
        ref={requestRef}
        className={classes.searchCompareBar}
        sx={theme => ({ borderTop: `1px solid ${theme.palette.primary.main}` })}
      >
        <Container sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
          <Button
            onClick={props.onClearAllClick}
            variant="outlined"
            size="small"
            sx={{ whiteSpace: 'nowrap' }}
          >
            Clear All
          </Button>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}
          >
            <Box
              sx={{
                display: 'grid',

                gridTemplateColumns: {
                  xs: '1fr ',
                  sm: '1fr 1fr',
                  lg: '1fr 1fr 1fr 1fr'
                },

                gap: 2
              }}
            >
              {[...Array(COMPARE_BULK_LIMIT)].map((x, index) => {
                const isEmpty: boolean = index >= props.compareItemList.length;
                return (
                  <div
                    className={cx(
                      classes.product,
                      isEmpty ? classes.emptyProduct : undefined
                    )}
                    key={index}
                  >
                    <Typography noWrap component="span" variant="body2">
                      {props.compareItemList[index]?.name}
                    </Typography>
                    {!isEmpty && (
                      <IconButton
                        onClick={() => {
                          props.onRemoveItemClick(
                            props.compareItemList[index]?.id
                          );
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </div>
                );
              })}
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2
              }}
            >
              {props.type === 'request' ? (
                <>
                  {(props.target === ProductType.INGREDIENTS || IS_UXBEAUTY) &&
                    (IS_UXBEAUTY ? (
                      <ToolTipContent
                        text={
                          verified
                            ? outOfStockProducts.length > 0
                              ? t('actionButtons.bulk.outOfStock', {
                                  products: outOfStockProducts.join(', ')
                                })
                              : t('actionButtons.bulk.selectOneRequest', {
                                  productType: getProductTypeSingulars(
                                    props.target
                                  ).toLowerCase(),
                                  type: RequestTypes.SAMPLE.toLowerCase()
                                })
                            : t('actionButtons.notVerified')
                        }
                        tooltip={isButtonDisabled(RequestTypes.SAMPLE)}
                      >
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() =>
                            handleRequestModal(
                              'addToCart',
                              LogEventType.OPENED_REQUEST_SAMPLE
                            )
                          }
                          disabled={isButtonDisabled(RequestTypes.SAMPLE)}
                          sx={{ gap: 1 }}
                        >
                          <ShoppingCartOutlined /> Request Samples
                        </Button>
                      </ToolTipContent>
                    ) : (
                      <></>
                    ))}

                  {((IS_UXBEAUTY &&
                    props.target !== ProductType.FORMULATIONS) ||
                    (!IS_UXBEAUTY &&
                      props.target !== ProductType.SERVICES)) && (
                    <ToolTipContent
                      text={
                        verified
                          ? t('actionButtons.bulk.selectOneRequest', {
                              productType: getProductTypeSingulars(
                                props.target
                              ).toLowerCase(),
                              type: RequestTypes.QUOTE.toLowerCase()
                            })
                          : t('actionButtons.notVerified')
                      }
                      tooltip={isButtonDisabled(RequestTypes.QUOTE)}
                    >
                      <Button
                        onClick={() =>
                          handleRequestModal(
                            'quote',
                            LogEventType.OPENED_REQUEST_QUOTE
                          )
                        }
                        variant="contained"
                        size="small"
                        disabled={isButtonDisabled(RequestTypes.QUOTE)}
                      >
                        Request Quotes
                      </Button>
                    </ToolTipContent>
                  )}
                  {(props.target === ProductType.INGREDIENTS ||
                    props.target === ProductType.FORMULATIONS) && (
                    <>
                      {(props.target === ProductType.INGREDIENTS ||
                        IS_UXBEAUTY) &&
                        (IS_UXBEAUTY ? (
                          <></>
                        ) : (
                          <ToolTipContent
                            text={
                              verified
                                ? outOfStockProducts.length > 0
                                  ? t('actionButtons.bulk.outOfStock', {
                                      products: outOfStockProducts.join(', ')
                                    })
                                  : t('actionButtons.bulk.selectOneRequest', {
                                      productType: getProductTypeSingulars(
                                        props.target
                                      ).toLowerCase(),
                                      type: RequestTypes.SAMPLE.toLowerCase()
                                    })
                                : t('actionButtons.notVerified')
                            }
                            tooltip={isButtonDisabled(RequestTypes.SAMPLE)}
                          >
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() =>
                                handleRequestModal(
                                  'sample',
                                  LogEventType.OPENED_REQUEST_SAMPLE
                                )
                              }
                              disabled={isButtonDisabled(RequestTypes.SAMPLE)}
                            >
                              Request Samples
                            </Button>
                          </ToolTipContent>
                        ))}
                      <ToolTipContent
                        tooltip={isButtonDisabled(RequestTypes.DOCUMENT)}
                        text={
                          verified
                            ? t('actionButtons.bulk.selectOneRequest', {
                                productType: getProductTypeSingulars(
                                  props.target
                                ).toLowerCase(),
                                type: RequestTypes.DOCUMENT.toLowerCase()
                              })
                            : t('actionButtons.notVerified')
                        }
                      >
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() =>
                            handleRequestModal(
                              'requestDocument',
                              LogEventType.OPENED_REQUEST_DOCUMENT
                            )
                          }
                          disabled={isButtonDisabled(RequestTypes.DOCUMENT)}
                        >
                          Request Documents
                        </Button>
                      </ToolTipContent>
                    </>
                  )}
                  <ToolTipContent
                    tooltip={isButtonDisabled(RequestTypes.CONTACT)}
                    text={
                      verified
                        ? t('actionButtons.bulk.selectOneContact', {
                            productType: getProductTypeSingulars(
                              props.target
                            ).toLowerCase()
                          })
                        : t('actionButtons.notVerified')
                    }
                  >
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() =>
                        handleRequestModal(
                          'contact',
                          LogEventType.OPENED_REQUEST_CONTACT
                        )
                      }
                      disabled={isButtonDisabled(RequestTypes.CONTACT)}
                    >
                      Contact All
                    </Button>
                  </ToolTipContent>
                </>
              ) : (
                <ToolTipContent
                  tooltip={props.compareItemList.length < 2 || !verified}
                  text={
                    verified
                      ? 'Please select at least two ingredient to compare between them.'
                      : t('actionButtons.notVerified')
                  }
                >
                  <Button
                    variant="contained"
                    size="small"
                    onClick={onCompareClickHandler}
                    disabled={props.compareItemList.length < 2 || !verified}
                  >
                    Compare
                  </Button>
                </ToolTipContent>
              )}
            </Box>
          </Box>
          {props.type === 'request' && props.compareItemList.length > 4 && (
            <div className={cx(classes.product, classes.plusIcon)}>
              <AddIcon fontSize="small" classes={{ root: classes.addIcon }} />
              {props.compareItemList.length - COMPARE_BULK_LIMIT > 0
                ? props.compareItemList.length - COMPARE_BULK_LIMIT
                : ''}
            </div>
          )}
          <IconButton onClick={props.endCompareRequestMode}>
            <CloseIcon />
          </IconButton>
        </Container>
      </Box>
      <SearchCompareModal
        show={showCompareModal}
        onHide={onCompareModalHide}
        compareItemList={props.compareItemList}
        onRemoveItemClick={props.onRemoveItemClick}
        searchRequest={props.searchRequest}
        target={props.target}
      />
      {requestModals === 'requestDocument' && (
        <RequestDocumentDialog
          productType={props.target}
          show={requestModals === 'requestDocument'}
          setModal={handleRequestModal}
          context={context}
          requestType={RequestTypes.DOCUMENT}
          searchRequest={props.searchRequest}
          fullProducts={props.compareItemList}
          searchContext={props.searchContext}
        />
      )}
      {requestModals === 'sample' && (
        <RequestQuoteOrSampleDialog
          productType={props.target}
          show={requestModals === 'sample'}
          setModal={handleRequestModal}
          context={context}
          requestType={RequestTypes.SAMPLE}
          fullProducts={props.compareItemList}
          searchContext={props.searchContext}
        />
      )}
      {requestModals === 'quote' && (
        <RequestQuoteOrSampleDialog
          productType={props.target}
          show={requestModals === 'quote'}
          setModal={handleRequestModal}
          context={context}
          requestType={RequestTypes.QUOTE}
          fullProducts={props.compareItemList}
          searchContext={props.searchContext}
        />
      )}
      {requestModals === 'contact' && (
        <RequestContactDialog
          show={requestModals === 'contact'}
          setModal={handleRequestModal}
          context={context}
          requestType={RequestTypes.CONTACT}
          fullProducts={props.compareItemList}
          searchContext={props.searchContext}
          productType={props.target}
        />
      )}
      {requestModals === 'addToCart' && (
        <AddToCartDialog
          open={requestModals === 'addToCart'}
          onClose={() => setRequestModals(undefined)}
          closeCompare={() => props.endCompareRequestMode()}
          products={props.compareItemList}
        />
      )}
    </>
  );
};

const useStyles = tss.create(({ theme }) => ({
  searchCompareBar: {
    backgroundColor: '#fff',
    maxHeight: '500px',
    transition: 'max-height 200ms',
    zIndex: 500,
    bottom: '0px',
    left: '0',
    width: "'100%'",
    position: 'fixed',
    padding: '8px 50px'
  },
  product: {
    minWidth: 175,
    alignItems: 'center',
    border: `1px solid ${theme.palette.primary.main}`,
    //would be cool if we could convince UX beauty to use a color similar to this
    background: IS_UXBEAUTY ? theme.palette.secondary.light : '#d9e6e6',
    display: 'flex',
    height: 32,
    padding: theme.spacing(1),
    gap: theme.spacing(2),
    justifyContent: 'space-between'
  },
  emptyProduct: {
    background: 'white'
  },
  addIcon: {
    color: 'inherit',
    '&:hover': {
      color: 'inherit',
      cursor: 'default'
    }
  },
  plusIcon: {
    width: 45,
    minWidth: 'unset',
    gap: 0,
    justifyContent: 'center'
  }
}));
